import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { todosAPI, trackingAPI } from '../utils/api';
import { TrendingUp, Activity, Clock, Target } from 'lucide-react';

const Analytics = ({ categories, tags }) => {
  const [todoStats, setTodoStats] = useState(null);
  const [trackingStats, setTrackingStats] = useState(null);
  const [timeRange, setTimeRange] = useState('week'); // week, month, quarter, year
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      const endDate = new Date();
      let startDate = new Date();
      
      switch (timeRange) {
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(startDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
      }

      const [todosRes, trackingRes] = await Promise.all([
        todosAPI.getAll(),
        trackingAPI.getStats({
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        })
      ]);

      const todos = todosRes.data;
      
      // Calculate todo statistics
      const stats = {
        total: todos.length,
        completed: todos.filter(t => t.status === 'completed').length,
        pending: todos.filter(t => t.status === 'pending').length,
        inProgress: todos.filter(t => t.status === 'in-progress').length,
        byCategory: {},
        byPriority: {
          high: todos.filter(t => t.priority === 'high').length,
          medium: todos.filter(t => t.priority === 'medium').length,
          low: todos.filter(t => t.priority === 'low').length
        },
        completionRate: todos.length > 0 ? Math.round((todos.filter(t => t.status === 'completed').length / todos.length) * 100) : 0
      };

      // Group by category
      categories.forEach(cat => {
        const catTodos = todos.filter(t => t.categoryId === cat.id);
        stats.byCategory[cat.name] = {
          total: catTodos.length,
          completed: catTodos.filter(t => t.status === 'completed').length,
          color: cat.color
        };
      });

      setTodoStats(stats);
      setTrackingStats(trackingRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="page-loading">Loading analytics...</div>;
  }

  const statusData = [
    { name: 'Completed', value: todoStats.completed, color: '#10b981' },
    { name: 'In Progress', value: todoStats.inProgress, color: '#f59e0b' },
    { name: 'Pending', value: todoStats.pending, color: '#64748b' }
  ];

  const priorityData = [
    { name: 'High', value: todoStats.byPriority.high, color: '#ef4444' },
    { name: 'Medium', value: todoStats.byPriority.medium, color: '#f59e0b' },
    { name: 'Low', value: todoStats.byPriority.low, color: '#10b981' }
  ];

  const categoryData = Object.entries(todoStats.byCategory).map(([name, data]) => ({
    name,
    total: data.total,
    completed: data.completed,
    color: data.color
  }));

  const activityData = trackingStats ? 
    Object.entries(trackingStats.byDate).map(([date, data]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      duration: Math.round(data.totalDuration / 60),
      activities: data.count
    })) : [];

  return (
    <div className="analytics-page">
      <div className="page-header">
        <h1>Analytics & Insights</h1>
        <select 
          value={timeRange} 
          onChange={(e) => setTimeRange(e.target.value)}
          className="time-range-select"
        >
          <option value="week">Last Week</option>
          <option value="month">Last Month</option>
          <option value="quarter">Last Quarter</option>
          <option value="year">Last Year</option>
        </select>
      </div>

      <div className="analytics-summary">
        <div className="summary-card">
          <div className="summary-icon" style={{ backgroundColor: '#6366f115' }}>
            <Target size={24} style={{ color: '#6366f1' }} />
          </div>
          <div className="summary-content">
            <h3>Completion Rate</h3>
            <div className="summary-value">{todoStats.completionRate}%</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ backgroundColor: '#10b98115' }}>
            <TrendingUp size={24} style={{ color: '#10b981' }} />
          </div>
          <div className="summary-content">
            <h3>Total Completed</h3>
            <div className="summary-value">{todoStats.completed}</div>
          </div>
        </div>

        {trackingStats && (
          <>
            <div className="summary-card">
              <div className="summary-icon" style={{ backgroundColor: '#f59e0b15' }}>
                <Activity size={24} style={{ color: '#f59e0b' }} />
              </div>
              <div className="summary-content">
                <h3>Activities Logged</h3>
                <div className="summary-value">{trackingStats.totalEntries}</div>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-icon" style={{ backgroundColor: '#8b5cf615' }}>
                <Clock size={24} style={{ color: '#8b5cf6' }} />
              </div>
              <div className="summary-content">
                <h3>Total Hours</h3>
                <div className="summary-value">{Math.round(trackingStats.totalDuration / 60)}</div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h3>Todo Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Priority Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={priorityData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {priorityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {categoryData.length > 0 && (
          <div className="chart-card full-width">
            <h3>Tasks by Category</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#6366f1" name="Total" />
                <Bar dataKey="completed" fill="#10b981" name="Completed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {activityData.length > 0 && (
          <div className="chart-card full-width">
            <h3>Activity Timeline</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="duration" 
                  stroke="#8b5cf6" 
                  name="Hours" 
                  strokeWidth={2}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="activities" 
                  stroke="#f59e0b" 
                  name="Activities" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
